import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Collapse,
  Input,
  Pagination,
  Select,
  Spin,
  Tag,
  Typography,
  Checkbox,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useGate } from "../Subscription/entitlements";
import EditDataModal from "./components/EditDataModal";
import {
  applyQuickClean,
  applySuggestion,
  downloadCleanedData,
  getChangelog,
  getCleaningPreview,
  getQualitySummary,
  getSuggestions,
  previewQuickClean,
  previewSuggestionApi,
  promoteRow,
  setRowWeight,
  updateRow,
} from "./services/dataCleaning.service";
import "./DataReviewPage.css";

const { Text, Title } = Typography;
const { Panel } = Collapse;

/* ── Types ── */

interface ColDef {
  column_name: string;
  inferred_type: string; // "object", "int64", "float64", etc.
}

// API row shape from cleaning/preview
interface ApiRow {
  row_index: number;
  values: Record<string, any>;
  weight: number | null;
  is_promoted: boolean;
}

interface QualityCard {
  key: string;
  label: string;
  issue_type: string;
  column_name: string | null;
  count: number;
}

interface ColType {
  column_name: string;
  inferred_type: string;
}

// Matches GET /datasets/{id}/suggestions response shape exactly
interface Suggestion {
  suggestion_id: string;
  rule_type: string;
  source: "rule" | "ai";
  column_name: string | null;
  rows_affected: number;
  summary: string;
  from_values: string[];
  to_value: string;
  details: Record<string, any>;
}

interface RecentChange {
  id: string;
  message: string;
  count: number;
  affectedCells?: string[]; // "rowIndex::columnName"
}

/* ── Helpers ── */

const toDisplayType = (inferred: string): "Text" | "Numeric" => {
  const t = (inferred ?? "").toLowerCase();
  if (t.includes("int") || t.includes("float") || t === "numeric" || t === "number")
    return "Numeric";
  return "Text";
};

const getSeverityColor = (label: string, count: number) => {
  if (count === 0) return "#52c41a";
  if (label.toLowerCase().includes("inconsistent") && count >= 15) return "#ff4d4f";
  return "#fa8c16";
};

const mapChangelog = (entries: any[]): RecentChange[] =>
  entries.map((e) => ({
    id: e.id,
    message: e.summary_json?.message ?? "",
    count: e.impact_json?.rows_affected ?? 0,
  }));

/* ── Component ── */

const DataReviewPage: React.FC = () => {
  const { t } = useTranslation("dataCleaning");
  const gate = useGate();
  const { datasetId } = useParams<{ id: string; datasetId: string }>();

  // Preview data (table)
  const [datasetName, setDatasetName] = useState("");
  const [columns, setColumns] = useState<ColDef[]>([]);
  const [rows, setRows] = useState<ApiRow[]>([]);
  const [summaryCards, setSummaryCards] = useState({
    total_rows: 0,
    missing_values: 0,
    inconsistencies: 0,
    duplicates: 0,
  });
  const [previewLoading, setPreviewLoading] = useState(true);

  // Table pagination
  const [tablePage, setTablePage] = useState(1);
  const [tableTotal, setTableTotal] = useState(0);
  const TABLE_PAGE_SIZE = 20;

  // Quality data (right panel)
  const [qualityCards, setQualityCards] = useState<QualityCard[]>([]);
  const [colTypes, setColTypes] = useState<ColType[]>([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityLoaded, setQualityLoaded] = useState(false);

  // Panel tabs
  const [rightTab, setRightTab] = useState<"quality" | "cleaning">("quality");

  // Suggestions — loaded from GET /suggestions
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);

  // Cell-level highlighting: Set of "rowIndex::columnName" strings
  const [highlightedCells, setHighlightedCells] = useState<Set<string>>(new Set());
  const [highlightedCount, setHighlightedCount] = useState<number | null>(null);

  // Suggestion preview state (null = none previewing)
  const [previewingSuggestionId, setPreviewingSuggestionId] = useState<string | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [loadingApplyId, setLoadingApplyId] = useState<string | null>(null);

  // Quick Clean state
  const [findValues, setFindValues] = useState<string[]>([]);
  const [findInput, setFindInput] = useState("");
  const [scope, setScope] = useState("__all__");
  const [replaceWith, setReplaceWith] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [quickCleanLoading, setQuickCleanLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<"preview" | "apply" | null>(null);

  // Recent changes
  const [recentChanges, setRecentChanges] = useState<RecentChange[]>([]);
  const [changelogLoaded, setChangelogLoaded] = useState(false);
  const [selectedChangeId, setSelectedChangeId] = useState<string | null>(null);
  const [currentCellIndex, setCurrentCellIndex] = useState(0);

  const selectedChange = recentChanges.find((c) => c.id === selectedChangeId);
  const selectedAffectedCells = selectedChange?.affectedCells ?? [];
  const currentCell = selectedAffectedCells[currentCellIndex] ?? null;

  // Edit row modal
  const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
  const [editRowValues, setEditRowValues] = useState<Record<string, any>>({});
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Row selection
  const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);

  // Inline weight editing
  const [editingWeightRow, setEditingWeightRow] = useState<number | null>(null);
  const [savingWeightRow, setSavingWeightRow] = useState<number | null>(null);
  const [weightInput, setWeightInput] = useState("");
  const weightInputRef = useRef<HTMLInputElement>(null);

  /* ── Data loading ── */

  const loadPreview = async (page = tablePage) => {
    if (!datasetId) return;
    setPreviewLoading(true);
    clearHighlights();
    try {
      const data = await getCleaningPreview(datasetId, page, TABLE_PAGE_SIZE);
      setDatasetName(data.dataset_name ?? "");
      setColumns(data.columns ?? []);
      setRows(data.rows ?? []);
      setTableTotal(data.total_rows ?? 0);
      setSummaryCards(
        data.summary_cards ?? {
          total_rows: 0,
          missing_values: 0,
          inconsistencies: 0,
          duplicates: 0,
        },
      );
    } catch {
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadQuality = async () => {
    if (!datasetId || qualityLoaded) return;
    setQualityLoading(true);
    try {
      const data = await getQualitySummary(datasetId);
      setQualityCards(data.cards ?? []);
      setColTypes(data.column_types ?? []);
      setQualityLoaded(true);
    } catch {
    } finally {
      setQualityLoading(false);
    }
  };

  const loadChangelog = async () => {
    if (!datasetId || changelogLoaded) return;
    try {
      const data = await getChangelog(datasetId);
      setRecentChanges(mapChangelog(Array.isArray(data) ? data : []));
      setChangelogLoaded(true);
    } catch {}
  };

  const loadSuggestions = async () => {
    if (!datasetId || suggestionsLoaded) return;
    try {
      const data = await getSuggestions(datasetId);
      setSuggestions(data.suggestions ?? []);
      setSuggestionsLoaded(true);
    } catch {}
  };

  useEffect(() => {
    setTablePage(1);
    loadPreview(1);
  }, [datasetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rightTab === "quality") loadQuality();
    if (rightTab === "cleaning") {
      loadChangelog();
      loadSuggestions();
    }
  }, [rightTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingWeightRow !== null && weightInputRef.current) {
      weightInputRef.current.focus();
    }
  }, [editingWeightRow]);

  // Auto-navigate to page when current cell changes
  useEffect(() => {
    if (currentCell && selectedChangeId) {
      const [rowIndexStr] = currentCell.split("::");
      const rowIndex = parseInt(rowIndexStr, 10);
      const page = Math.floor(rowIndex / TABLE_PAGE_SIZE) + 1;
      if (page !== tablePage) {
        handleTablePageChange(page);
      }
    }
  }, [currentCell, selectedChangeId]);

  // Auto-scroll table horizontally to show highlighted cell
  useEffect(() => {
    if (currentCell && selectedChangeId) {
      setTimeout(() => {
        const highlightedCell = document.querySelector(
          `[data-highlighted-cell="${currentCell}"]`
        ) as HTMLTableCellElement;
        if (highlightedCell && highlightedCell.scrollIntoView) {
          highlightedCell.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }
      }, 50);
    }
  }, [currentCell, selectedChangeId]);

  /* ── Quick Clean ── */

  const canAct = findValues.length > 0 && replaceWith.trim() !== "";

  const handleAddFindValue = () => {
    const val = findInput.trim();
    if (val && !findValues.includes(val)) setFindValues((p) => [...p, val]);
    setFindInput("");
  };

  const clearHighlights = () => {
    setHighlightedCells(new Set());
    setHighlightedCount(null);
    setPreviewingSuggestionId(null);
    setIsPreviewing(false);
  };

  const handlePreviewQuickClean = async () => {
    if (!datasetId || !canAct) return;
    setPreviewingSuggestionId(null); // clear any suggestion preview
    setQuickCleanLoading(true);
    setLoadingAction("preview");
    try {
      const cells = new Set<string>();
      let totalAffected = 0;

      for (const fv of findValues) {
        const result = await previewQuickClean(datasetId, {
          find_value: fv,
          scope,
          replace_with: replaceWith,
        });
        totalAffected += result.rows_affected ?? 0;
        // Use cell-level data from API: before_after_preview[].{row_index, column_name}
        (result.before_after_preview ?? []).forEach((p: any) => {
          cells.add(`${p.row_index}::${p.column_name}`);
        });
      }

      setHighlightedCells(cells);
      setHighlightedCount(totalAffected);
      setIsPreviewing(true);
    } catch {
    } finally {
      setQuickCleanLoading(false);
      setLoadingAction(null);
    }
  };

  const handleApplyQuickClean = async () => {
    if (!datasetId || !canAct) return;
    setQuickCleanLoading(true);
    setLoadingAction("apply");
    try {
      // First, preview all changes to capture affected cells
      const cellsToCapture = new Set<string>();
      let totalAffected = 0;

      for (const fv of findValues) {
        const previewResult = await previewQuickClean(datasetId, {
          find_value: fv,
          scope,
          replace_with: replaceWith,
        });
        totalAffected += previewResult.rows_affected ?? 0;
        (previewResult.before_after_preview ?? []).forEach((p: any) => {
          cellsToCapture.add(`${p.row_index}::${p.column_name}`);
        });
      }

      // Then apply the changes
      for (const fv of findValues) {
        await applyQuickClean(datasetId, {
          find_value: fv,
          scope,
          replace_with: replaceWith,
        });
      }

      setRecentChanges((prev) => [
        {
          id: Date.now().toString(),
          message: `${findValues.join(", ")} → ${replaceWith}`,
          count: totalAffected,
          affectedCells: Array.from(cellsToCapture),
        },
        ...prev,
      ]);

      // Reset form + highlights
      setFindValues([]);
      setReplaceWith("");
      setScope("__all__");
      clearHighlights();

      // Refresh table from page 1 + invalidate quality so it reloads
      setTablePage(1);
      setQualityLoaded(false);
      await loadPreview(1);
    } catch {
    } finally {
      setQuickCleanLoading(false);
      setLoadingAction(null);
    }
  };

  /* ── Suggestions ── */

  // API preview: POST /suggestions/preview → cell-level changes
  const handlePreviewSuggestion = async (s: Suggestion) => {
    if (!datasetId) return;
    setLoadingPreviewId(s.suggestion_id);
    try {
      const result = await previewSuggestionApi(datasetId, {
        suggestion_id: s.suggestion_id,
        rule_type: s.rule_type,
        details: s.details,
      });
      const cells = new Set<string>();
      (result.changes ?? []).forEach((c: any) => {
        if (c.column_name) cells.add(`${c.row_index}::${c.column_name}`);
      });
      setHighlightedCells(cells);
      setHighlightedCount(result.rows_affected ?? cells.size);
      setPreviewingSuggestionId(s.suggestion_id);
    } catch {
    } finally {
      setLoadingPreviewId(null);
    }
  };

  const handleApplySuggestion = async (s: Suggestion) => {
    if (!datasetId) return;
    setLoadingApplyId(s.suggestion_id);
    try {
      // First preview to capture affected cells
      const previewResult = await previewSuggestionApi(datasetId, {
        suggestion_id: s.suggestion_id,
        rule_type: s.rule_type,
        details: s.details,
      });
      const affectedCells = new Set<string>();
      (previewResult.changes ?? []).forEach((c: any) => {
        if (c.column_name) affectedCells.add(`${c.row_index}::${c.column_name}`);
      });

      // Then apply
      const result = await applySuggestion(datasetId, {
        suggestion_id: s.suggestion_id,
        rule_type: s.rule_type,
        details: s.details,
      });
      setRecentChanges((prev) => [
        {
          id: Date.now().toString(),
          message: result.message ?? s.summary,
          count: result.rows_affected ?? s.rows_affected,
          affectedCells: Array.from(affectedCells),
        },
        ...prev,
      ]);
      setSuggestions((prev) =>
        prev.filter((x) => x.suggestion_id !== s.suggestion_id),
      );
      clearHighlights();
      setTablePage(1);
      setQualityLoaded(false);
      await loadPreview(1);
      gate.refresh();
    } catch {
    } finally {
      setLoadingApplyId(null);
    }
  };

  /* ── Row editing ── */

  const handleOpenEdit = (row: ApiRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditRowIndex(row.row_index);
    setEditRowValues({ ...row.values });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (
    rowIndex: number,
    values: Record<string, any>,
    changedColumns: string[],
  ) => {
    if (!datasetId) return;
    try {
      await updateRow(datasetId, rowIndex, values, changedColumns);
      setRows((prev) =>
        prev.map((r) =>
          r.row_index === rowIndex ? { ...r, values: { ...r.values, ...values } } : r,
        ),
      );
      setEditModalOpen(false);
    } catch {}
  };

  /* ── Weight cell ── */

  const handleWeightClick = (row: ApiRow) => {
    setEditingWeightRow(row.row_index);
    setWeightInput(row.weight != null ? String(row.weight) : "");
  };

  const handleWeightSave = async (rowIndex: number) => {
    if (!datasetId) return;

    const trimmed = weightInput.trim();
    // Empty → clear weight (null)
    const numVal = trimmed === "" ? null : Number(trimmed);

    // Validate: must be a positive number or null (to clear)
    if (numVal !== null && (isNaN(numVal) || numVal <= 0)) {
      toast.error(t("reviewPage.toasts.weightMustBePositive"));
      setEditingWeightRow(null);
      return;
    }

    setEditingWeightRow(null);
    setSavingWeightRow(rowIndex);
    try {
      const result = await setRowWeight(datasetId, rowIndex, numVal);
      setRows((prev) =>
        prev.map((r) =>
          r.row_index === rowIndex
            ? { ...r, weight: result.weight, is_promoted: result.is_promoted }
            : r,
        ),
      );
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? t("reviewPage.toasts.weightSaveFailed");
      toast.error(msg);
    } finally {
      setSavingWeightRow(null);
    }
  };

  /* ── Star / promote ── */

  const handleToggleStar = async (row: ApiRow) => {
    if (!datasetId) return;
    if (row.weight == null) return; // Cannot promote without weight
    try {
      const result = await promoteRow(datasetId, row.row_index);
      setRows((prev) =>
        prev.map((r) =>
          r.row_index === row.row_index
            ? { ...r, is_promoted: result.is_promoted }
            : r,
        ),
      );
    } catch {}
  };

  /* ── Download ── */

  const handleDownload = async () => {
    if (!datasetId) return;
    try {
      const blob = await downloadCleanedData(datasetId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${datasetName || "cleaned_data"}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  /* ── Table pagination ── */

  const handleTablePageChange = (page: number) => {
    setTablePage(page);
    loadPreview(page);
  };

  /* ── Derived values ── */

  const isCellHighlighted = (rowIndex: number, colName: string): boolean => {
    const cellKey = `${rowIndex}::${colName}`;
    // If a change is selected, only highlight the CURRENT cell
    if (selectedChangeId && currentCell === cellKey) {
      return true;
    }
    // Otherwise highlight cells from preview
    return highlightedCells.has(cellKey);
  };

  const totalIssues = qualityCards.reduce((sum, c) => sum + c.count, 0);

  const scopeOptions = [
    { label: t("reviewPage.quickClean.allFields"), value: "__all__" },
    ...columns.map((c) => ({ label: c.column_name, value: c.column_name })),
  ];

  /* ── Render ── */

  if (previewLoading) {
    return (
      <div className="dr-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dr-page">
      <div className="dr-card">
        {/* Header */}
        <div className="dr-header">
          <div className="dr-header__left">
            <Title level={4} className="dr-header__title">
              {t("reviewPage.title")}
            </Title>
            {datasetName && (
              <Tag className="dr-header__filename">{datasetName}</Tag>
            )}
          </div>
          <Button
            className="dr-btn dr-btn--outlined"
            icon={<DownloadOutlined />}
            onClick={() =>
              gate.requirePaid(handleDownload, t("reviewPage.exportGateReason"))
            }
          >
            {t("reviewPage.downloadCleanedData")}
          </Button>
        </div>

        {/* Stats */}
        <div className="dr-stats">
          {[
            { label: t("reviewPage.stats.totalRows"), value: summaryCards.total_rows },
            { label: t("reviewPage.stats.missingValues"), value: summaryCards.missing_values },
            { label: t("reviewPage.stats.inconsistencies"), value: summaryCards.inconsistencies },
            { label: t("reviewPage.stats.duplicates"), value: summaryCards.duplicates },
          ].map((s) => (
            <div key={s.label} className="dr-stat-card">
              <Text className="dr-stat-card__label">{s.label}</Text>
              <div className="dr-stat-card__value">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Split layout */}
        <div className="dr-content">
          {/* Left: data table */}
          <div className="dr-table-side">
            <div className="dr-table-scroll">
              <table className="dr-data-table">
                <thead>
                  <tr>
                    <th className="dr-th dr-th--checkbox">
                      <Checkbox
                        checked={
                          rows.length > 0 &&
                          selectedRowIndices.length === rows.length
                        }
                        indeterminate={
                          selectedRowIndices.length > 0 &&
                          selectedRowIndices.length < rows.length
                        }
                        onChange={(e) =>
                          setSelectedRowIndices(
                            e.target.checked ? rows.map((r) => r.row_index) : [],
                          )
                        }
                      />
                    </th>
                    {/* Weight — special metadata column */}
                    <th className="dr-th">{t("reviewPage.table.weight")}</th>
                    {columns.map((col) => (
                      <th key={col.column_name} className="dr-th">
                        {col.column_name}
                      </th>
                    ))}
                    <th className="dr-th dr-th--actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isEditingWeight = editingWeightRow === row.row_index;

                    return (
                      <tr key={row.row_index} className="dr-tr">
                        {/* Checkbox */}
                        <td className="dr-td dr-td--checkbox">
                          <Checkbox
                            checked={selectedRowIndices.includes(row.row_index)}
                            onChange={(e) =>
                              setSelectedRowIndices((prev) =>
                                e.target.checked
                                  ? [...prev, row.row_index]
                                  : prev.filter((i) => i !== row.row_index),
                              )
                            }
                          />
                        </td>

                        {/* Weight cell — inline editable */}
                        <td
                          className="dr-td dr-td--numeric"
                          onClick={() =>
                            !isEditingWeight &&
                            savingWeightRow !== row.row_index &&
                            handleWeightClick(row)
                          }
                        >
                          {isEditingWeight ? (
                            <input
                              ref={weightInputRef}
                              className="dr-cell-input"
                              type="text"
                              inputMode="numeric"
                              placeholder={t("reviewPage.table.weightPlaceholder")}
                              value={weightInput}
                              onChange={(e) => setWeightInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleWeightSave(row.row_index);
                                }
                                if (e.key === "Escape") setEditingWeightRow(null);
                              }}
                              onBlur={() => {
                                // save on blur if a value was typed
                                if (weightInput.trim() !== "") {
                                  handleWeightSave(row.row_index);
                                } else {
                                  setEditingWeightRow(null);
                                }
                              }}
                            />
                          ) : savingWeightRow === row.row_index ? (
                            <span className="dr-weight-saving">
                              <Spin size="small" />
                            </span>
                          ) : (
                            <span className={row.weight == null ? "dr-cell--empty" : ""}>
                              {row.weight ?? "-"}
                            </span>
                          )}
                        </td>

                        {/* Data columns */}
                        {columns.map((col) => {
                          const val = row.values[col.column_name];
                          const highlighted = isCellHighlighted(
                            row.row_index,
                            col.column_name,
                          );
                          const cellKey = `${row.row_index}::${col.column_name}`;
                          return (
                            <td
                              key={col.column_name}
                              className={`dr-td${highlighted ? " dr-td--highlighted" : ""}`}
                              data-highlighted-cell={highlighted ? cellKey : undefined}
                            >
                              <span className={val == null || val === "" ? "dr-cell--empty" : ""}>
                                {val ?? "-"}
                              </span>
                            </td>
                          );
                        })}

                        {/* Actions */}
                        <td className="dr-td dr-td--actions">
                          <button
                            className="dr-action-btn"
                            onClick={(e) => handleOpenEdit(row, e)}
                          >
                            <EditOutlined />
                          </button>
                          <button
                            className={`dr-action-btn${row.is_promoted ? " dr-action-btn--starred" : ""}${row.weight == null ? " dr-action-btn--disabled" : ""}`}
                            onClick={() => handleToggleStar(row)}
                            title={row.weight == null ? t("reviewPage.table.promoteTooltip") : ""}
                          >
                            {row.is_promoted ? <StarFilled /> : <StarOutlined />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Table pagination */}
            {tableTotal > TABLE_PAGE_SIZE && (
              <div className="dr-table-pagination">
                <Pagination
                  current={tablePage}
                  pageSize={TABLE_PAGE_SIZE}
                  total={tableTotal}
                  showSizeChanger={false}
                  onChange={handleTablePageChange}
                />
              </div>
            )}
          </div>

          {/* Right: analysis panel */}
          <div className="dr-panel">
            <div className="dr-panel-tabs">
              <button
                className={`dr-panel-tab${rightTab === "quality" ? " dr-panel-tab--active" : ""}`}
                onClick={() => setRightTab("quality")}
              >
                {t("reviewPage.tabs.qualityCheck")}{" "}
                <span className="dr-panel-tab__badge">{totalIssues}</span>
              </button>
              <button
                className={`dr-panel-tab${rightTab === "cleaning" ? " dr-panel-tab--active" : ""}`}
                onClick={() => setRightTab("cleaning")}
              >
                {t("reviewPage.tabs.dataCleaning")}
              </button>
            </div>

            {/* Quality check tab */}
            {rightTab === "quality" && (
              <div className="dr-panel-body">
                {qualityLoading ? (
                  <div className="dr-panel-loading">
                    <Spin size="small" />
                  </div>
                ) : (
                  <>
                    <Collapse
                      defaultActiveKey={["quality-summary"]}
                      ghost
                      className="dr-collapse"
                    >
                      <Panel
                        header={
                          <Text strong className="dr-collapse-header">
                            {t("reviewPage.quality.summaryTitle")}
                          </Text>
                        }
                        key="quality-summary"
                      >
                        <div className="dr-quality-list">
                          {qualityCards.map((card) => (
                            <div key={card.key} className="dr-quality-item">
                              <Text className="dr-quality-item__label">
                                {card.label}
                              </Text>
                              <span
                                className="dr-quality-item__badge"
                                style={{
                                  background: getSeverityColor(card.label, card.count),
                                }}
                              >
                                {card.count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </Panel>
                    </Collapse>

                    <Collapse
                      defaultActiveKey={["column-types"]}
                      ghost
                      className="dr-collapse"
                    >
                      <Panel
                        header={
                          <Text strong className="dr-collapse-header">
                            {t("reviewPage.quality.columnTypesTitle")}
                          </Text>
                        }
                        key="column-types"
                      >
                        <div className="dr-column-types">
                          {colTypes.map((ct) => (
                            <div key={ct.column_name} className="dr-column-type-item">
                              <Text className="dr-column-type-item__name">
                                {ct.column_name}
                              </Text>
                              <Tag className="dr-type-tag">
                                {toDisplayType(ct.inferred_type)}
                              </Tag>
                            </div>
                          ))}
                        </div>
                      </Panel>
                    </Collapse>
                  </>
                )}
              </div>
            )}

            {/* Data Cleaning tab */}
            {rightTab === "cleaning" && (
              <div className="dr-panel-body">
                {/* Suggestions */}
                <Collapse
                  defaultActiveKey={["suggestions"]}
                  ghost
                  className="dr-collapse"
                >
                  <Panel
                    header={
                      <Text strong className="dr-collapse-header">
                        {t("reviewPage.suggestions.title")}
                      </Text>
                    }
                    key="suggestions"
                  >
                    {!suggestionsLoaded ? (
                      <div className="dr-panel-loading">
                        <Spin size="small" />
                      </div>
                    ) : suggestions.length === 0 ? (
                      <Text className="dr-recent-changes__empty">
                        {t("reviewPage.suggestions.empty")}
                      </Text>
                    ) : (
                      <div className="dr-suggestions">
                        {suggestions.map((s) => {
                          const isSuggPreviewing =
                            previewingSuggestionId === s.suggestion_id;
                          return (
                            <div
                              key={s.suggestion_id}
                              className="dr-suggestion-card"
                            >
                              {s.source === "ai" && (
                                <span className="dr-suggestion-badge--ai">
                                  ✦ {t("reviewPage.suggestions.aiBadge")}
                                </span>
                              )}
                              {s.from_values.length > 0 && (
                                <div className="dr-suggestion-card__values">
                                  <Text className="dr-suggestion-card__from">
                                    {s.from_values.join(", ")}
                                  </Text>
                                  <div className="dr-suggestion-card__arrow">↓</div>
                                  <Text className="dr-suggestion-card__to">
                                    {s.to_value}
                                  </Text>
                                </div>
                              )}
                              <div className="dr-suggestion-card__footer">
                                <Text className="dr-suggestion-card__meta">
                                  {s.summary}
                                </Text>
                                {isSuggPreviewing ? (
                                  <Button
                                    size="small"
                                    type="primary"
                                    className="dr-suggestion-card__btn dr-suggestion-card__btn--apply"
                                    loading={loadingApplyId === s.suggestion_id}
                                    onClick={() =>
                          gate.requireTokens(0, () => handleApplySuggestion(s))
                        }
                                  >
                                    {t("actions.apply")}
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    className="dr-suggestion-card__btn"
                                    loading={loadingPreviewId === s.suggestion_id}
                                    onClick={() => handlePreviewSuggestion(s)}
                                  >
                                    {t("actions.preview")}
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Panel>
                </Collapse>

                {/* Quick Clean */}
                <div className="dr-quick-clean">
                  <Text strong className="dr-quick-clean__title">
                    {t("reviewPage.quickClean.title")}
                  </Text>
                  <Text className="dr-quick-clean__subtitle">
                    {t("reviewPage.quickClean.subtitle")}
                  </Text>

                  <div className="dr-field">
                    <Text className="dr-field__label">{t("reviewPage.quickClean.findValuesLabel")}</Text>
                    <div className="dr-chip-input">
                      {findValues.map((val) => (
                        <span key={val} className="dr-chip">
                          {val}
                          <CloseOutlined
                            className="dr-chip__remove"
                            onClick={() =>
                              setFindValues((prev) => prev.filter((v) => v !== val))
                            }
                          />
                        </span>
                      ))}
                      <input
                        className="dr-chip-input__input"
                        placeholder={
                          findValues.length === 0 ? t("reviewPage.quickClean.findValuesPlaceholder") : ""
                        }
                        value={findInput}
                        onChange={(e) => setFindInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddFindValue();
                        }}
                        onBlur={() => {
                          if (findInput.trim()) {
                            handleAddFindValue();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="dr-field">
                    <Text className="dr-field__label">{t("reviewPage.quickClean.inLabel")}</Text>
                    <Select
                      className="dr-field__select"
                      value={scope}
                      onChange={(val) => setScope(val)}
                      options={scopeOptions}
                    />
                  </div>

                  <div className="dr-field">
                    <Text className="dr-field__label">{t("reviewPage.quickClean.replaceWithLabel")}</Text>
                    <Input
                      className="dr-field__input"
                      placeholder={t("reviewPage.quickClean.replaceWithPlaceholder")}
                      value={replaceWith}
                      onChange={(e) => setReplaceWith(e.target.value)}
                    />
                  </div>

                  {isPreviewing && highlightedCount !== null && (
                    <Text className="dr-preview-count">
                      {t("reviewPage.quickClean.previewCount", { count: highlightedCount })}
                    </Text>
                  )}

                  <div className="dr-quick-clean__actions">
                    <Button
                      className="dr-qc-btn"
                      disabled={!canAct}
                      loading={loadingAction === "preview"}
                      onClick={handlePreviewQuickClean}
                    >
                      {t("actions.preview")}
                    </Button>
                    <Button
                      className="dr-qc-btn dr-qc-btn--primary"
                      type="primary"
                      disabled={!canAct}
                      loading={loadingAction === "apply"}
                      onClick={handleApplyQuickClean}
                    >
                      {t("actions.apply")}
                    </Button>
                  </div>
                </div>

                {/* Recent Changes */}
                <div className="dr-recent-changes">
                  <Text strong className="dr-recent-changes__title">
                    {t("reviewPage.recentChanges.title")}
                  </Text>
                  {recentChanges.length === 0 ? (
                    <Text className="dr-recent-changes__empty">
                      {t("reviewPage.recentChanges.empty")}
                    </Text>
                  ) : (
                    <div className="dr-recent-changes__list">
                      {recentChanges.map((change) => {
                        const isSelected = selectedChangeId === change.id;
                        const affectedCells = change.affectedCells ?? [];
                        return (
                          <div
                            key={change.id}
                            className={`dr-change-card${isSelected ? " dr-change-card--selected" : ""}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedChangeId(null);
                              } else {
                                setSelectedChangeId(change.id);
                                setCurrentCellIndex(0);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="dr-change-card__values">
                              <Text className="dr-change-card__message">
                                {change.message}
                              </Text>
                            </div>
                            <div className="dr-change-card__footer">
                              <Text className="dr-change-card__count">
                                {t("reviewPage.recentChanges.valuesCount", { count: change.count })}
                              </Text>
                              <span className="dr-change-card__applied">
                                <CheckOutlined /> {t("reviewPage.recentChanges.applied")}
                              </span>
                            </div>
                            {isSelected && affectedCells.length > 0 && (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                  <Text style={{ fontSize: 12, color: "#666" }}>
                                    {currentCellIndex + 1} / {affectedCells.length}
                                  </Text>
                                  <div style={{ display: "flex", gap: 8 }}>
                                    <Button
                                      type="text"
                                      size="small"
                                      disabled={currentCellIndex === 0}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setCurrentCellIndex(Math.max(0, currentCellIndex - 1));
                                        (e.currentTarget as HTMLButtonElement).blur();
                                      }}
                                    >
                                      ← Prev
                                    </Button>
                                    <Button
                                      type="text"
                                      size="small"
                                      disabled={currentCellIndex === affectedCells.length - 1}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setCurrentCellIndex(Math.min(affectedCells.length - 1, currentCellIndex + 1));
                                        (e.currentTarget as HTMLButtonElement).blur();
                                      }}
                                    >
                                      Next →
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <EditDataModal
        open={editModalOpen}
        rowIndex={editRowIndex}
        values={editRowValues}
        columns={columns}
        onSave={handleSaveEdit}
        onClose={() => setEditModalOpen(false)}
      />
    </div>
  );
};

export default DataReviewPage;
